import { FC, useCallback, useMemo } from 'react';
import { ModalContainer, ModalHeader } from '@/utils/modal';
import { AddSamplePlansForm } from '@/components/Samples/AddSamplePlansForm';
import { AddSamplePlanProvider } from '@/components/Samples/AddSamplePlanProvider';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { StudyApiId } from '@/model/Study.model';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { DatabaseId } from '@/model/Common.model';
import Loading from '@/components/Loading/Loading';
import { StudyGroupV1 } from '@/model/TreatmentGroup.model';
import { errorToast, successToast } from '@/helpers';
import {
  PlannedAnimalTarget,
  RegisteredAnimalTarget,
  SampleDefinitionFormData,
  SampleDefinitionTargetFormData,
  SampleDefinitionUpsertV1,
  SampleDefinitionV1,
} from '@/model/SamplePlan.model';
import { isApiId } from '@/utils/ApiId.utils';
import { SampleDefinitionTargetV1 } from '@/components/Samples/AssignAnimals/AssignAnimal.util';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model.ts';

type AddSamplePlansFormModalProps = {
  studyId: DatabaseId;
  studyApiId: StudyApiId;
  sampleDefinitions: Array<SampleDefinitionV1>;
  onSampleDefinitionsUpdate: () => void;
  closeModal: () => void;
};
const toSampleDefinitionTargetV1 = (
  data: SampleDefinitionTargetFormData,
  planned_animals: Array<PlannedAnimalTarget>
): SampleDefinitionTargetV1 => {
  switch (data.type) {
    case 'groups': {
      return { type: data.type, targets: data.targets.filter((api_id) => isApiId('grp', api_id, false)) };
    }
    case 'registered_animals': {
      return { type: data.type, targets: data.targets.filter((api_id) => isApiId('aml', api_id, false)) };
    }
    case 'planned_animals': {
      // Filter out deleted animal definitions by checking if they exist in current planned_animals
      const validAnimalDefinitionIds = new Set(planned_animals.map((animal) => animal.api_id));
      return {
        type: data.type,
        targets: data.targets.filter(
          (api_id) => isApiId('adf', api_id, false) && validAnimalDefinitionIds.has(api_id)
        ) as Array<AnimalDefinitionApiId>,
      };
    }
    default: {
      return { type: 'no_target' };
    }
  }
};
const toSampleDefinitionUpsertV1 = (
  data: SampleDefinitionFormData,
  planned_animals: Array<PlannedAnimalTarget>
): SampleDefinitionUpsertV1 => {
  const { api_id, ...rest } = data;
  if (isApiId('sdf', api_id, false)) {
    return { api_id, ...rest, target: toSampleDefinitionTargetV1(rest.target, planned_animals) };
  }
  return { ...rest, target: toSampleDefinitionTargetV1(rest.target, planned_animals) };
};

export const AddSamplePlansFormModal: FC<AddSamplePlansFormModalProps> = ({
  studyId,
  studyApiId,
  sampleDefinitions,
  onSampleDefinitionsUpdate,
  closeModal,
}) => {
  const { loading: registeredAnimalsLoading, response: registeredAnimalsResponse } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyId}/animals',
    path: { studyId: studyApiId },
    query: { include: ['study_group'], perPage: -1 },
    invokeOnInit: true,
    options: { onError: { slug: 'sample-plan-crud-registered-animal-load' } },
  });
  const { loading: plannedAnimalsLoading, response: plannedAnimalsResponse } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/animals',
    path: { studyApiId },
    query: { perPage: -1 },
    options: { onError: { slug: 'sample-plan-crud-planned-animal-load' } },
  });
  const { loading: groupsLoading, response: groupsResponse } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.groups.list',
    path: { id: studyId },
    query: { perPage: -1 },
    options: { onError: { slug: 'sample-plan-crud-groups-load' } },
  });
  const { invoke: upsertSampleDefinitions } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/upsert',
    path: { studyApiId },
    invokeOnInit: false,
    options: {
      onError: {
        slug: 'sample-plan-crud-sampled-definition-upsert',
        capture: true,
        throw: false,
        toast: false,
      },
    },
  });
  const { invoke: removeSampleDefinitions } = useApiHook({
    endpoint: 'DELETE /api/v1/studies/{studyApiId}/planned/samples/remove',
    path: { studyApiId },
    invokeOnInit: false,
    options: {
      onError: {
        slug: 'sample-plan-crud-sampled-definition-remove',
        capture: true,
        throw: false,
        toast: false,
      },
    },
  });
  const planned_animals = useMemo<Array<PlannedAnimalTarget>>(() => {
    if (plannedAnimalsResponse?.type === 'success') {
      return plannedAnimalsResponse.body.data.map((def) => ({
        api_id: def.api_id,
        animal_api_id: def.animal_api_id,
        planned_id: def.planned_id,
        study_group_api_id: def.study_group_api_id as PlannedAnimalTarget['study_group_api_id'],
      }));
    }
    return [];
  }, [plannedAnimalsResponse]);
  const registered_animals = useMemo<Array<RegisteredAnimalTarget>>(() => {
    if (registeredAnimalsResponse?.type === 'success') {
      return registeredAnimalsResponse.body.data.map((animal) => ({
        api_id: animal.api_id,
        name: animal.name,
        study_group_api_id: animal.study_group?.api_id,
      }));
    }
    return [];
  }, [registeredAnimalsResponse]);

  const handleSubmit = useCallback(
    async (sampleDefinitions: Array<SampleDefinitionFormData>, deleted: Set<SampleDefinitionFormData['api_id']>) => {
      const idsToDelete = Array.from(deleted).filter((api_id) => isApiId('sdf', api_id, false));

      // First, remove deleted sample definitions to free up their names
      let removeSuccess = true;
      if (idsToDelete.length > 0) {
        const removeResponse = await removeSampleDefinitions({ path: { studyApiId }, body: idsToDelete });
        if (removeResponse.type === 'success') {
          successToast(`Deleted ${idsToDelete.length} planned sample groups`);
        } else {
          errorToast(`Could not delete ${idsToDelete.length} planned sample groups`);
          removeSuccess = false;
        }
      }

      // Only proceed with upsert if remove succeeded (or there was nothing to remove)
      if (!removeSuccess) {
        return;
      }

      // Then, upsert the remaining/updated sample definitions
      const upsertResponse = await upsertSampleDefinitions({
        path: { studyApiId },
        body: sampleDefinitions.map((sampleDef) => toSampleDefinitionUpsertV1(sampleDef, planned_animals)),
      });

      if (upsertResponse.type === 'success') {
        successToast('Sample plan updated successfully');
        onSampleDefinitionsUpdate();
        closeModal();
      } else {
        errorToast(`Could not update ${sampleDefinitions.length} planned sample groups`);
      }
    },
    [
      studyApiId,
      upsertSampleDefinitions,
      removeSampleDefinitions,
      onSampleDefinitionsUpdate,
      closeModal,
      planned_animals,
    ]
  );

  if (
    groupsLoading ||
    registeredAnimalsLoading ||
    plannedAnimalsLoading ||
    groupsResponse?.type !== 'success' ||
    registeredAnimalsResponse?.type !== 'success' ||
    plannedAnimalsResponse?.type !== 'success'
  ) {
    return <Loading />;
  }

  return (
    <ModalContainer size="wide">
      <ModalHeader
        title="Sampling plan"
        subText={
          <>
            Create a sampling plan by defining sample types, specifying metadata, and assigning them to groups or
            animals.{' '}
            <a
              className="link"
              href="https://help.benchling.com/hc/en-us/articles/41869571707533-Benchling-In-Vivo-Animal-and-Sample-Planning-Limited-Availability"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read more
            </a>
            .
          </>
        }
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        subTextClasses="f6 lh-copy mt2"
      />
      <div className="mh4 mt3">
        <AddSamplePlanProvider
          groups={(groupsResponse.body as { data: Array<StudyGroupV1> }).data}
          registered_animals={registered_animals}
          planned_animals={planned_animals}
          sample_definitions={sampleDefinitions}
          onSubmit={(sampleDefinitions, deleted) => handleSubmit(sampleDefinitions, deleted)}
        >
          <AddSamplePlansForm />
        </AddSamplePlanProvider>
      </div>
    </ModalContainer>
  );
};
